// hooks/billing/useAccountBalance.js
import { useCallback, useEffect, useState } from 'react';
import BillingService from '../../services/billing.service';

/**
 * useAccountBalance — `GET /accounts/{id}/billing/balance`.
 *
 * The response (camelCase from the backend's alias generator) includes
 * `totalBalance`, `periodBalance`, `extraBalance`, `tier` and
 * `organization.isEnterprisePool`. The hook returns the parsed body
 * unchanged so callers can read whichever field they need.
 *
 * @param {string} accountId
 * @param {Object} [options]
 * @param {boolean} [options.auto=true] auto-fetch when accountId changes
 */
export function useAccountBalance(accountId, { auto = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(auto && accountId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    const result = await BillingService.getAccountBalance(accountId);
    if (result.success) {
      setData(result.data);
    } else {
      setError(result);
    }
    setLoading(false);
  }, [accountId]);

  useEffect(() => {
    if (auto && accountId) {
      refetch();
    } else if (!accountId) {
      setData(null);
      setError(null);
      setLoading(false);
    }
  }, [auto, accountId, refetch]);

  return { data, loading, error, refetch };
}

export default useAccountBalance;
